/**
 * Crest-line extraction types. See
 * docs/superpowers/specs/2026-09-02-crest-line-extraction-design.md.
 *
 * All coordinates are project world coordinates, Z-up, Float64 — these run at
 * UTM scale where Float32 jitter is visible.
 */

/** How a cross-section was resolved. See spec §3 and §7.1. */
export type SectionKind = 'flat' | 'relief';

export interface CrestParams {
  /** Spacing of cross-sections along the guide, metres. */
  stationInterval: number;
  /** Half-width of the searched corridor either side of the guide, metres. */
  corridorHalfWidth: number;
  /** Sampling resolution across a section, metres. */
  offsetStep: number;
  /**
   * Top-region relief at or below which the section is FLAT and the crest is
   * the top's midpoint. 50 mm, not 5 mm: a section skewed by θ picks up
   * `grade × width × sin θ` of spurious elevation (~21 mm at 1 % grade, 12 m
   * top, 10° skew), so a tighter value sits below the method's noise floor.
   * Spec §7.2.
   */
  flatTolerance: number;
  /** Width of the tied set within a relief section, metres. */
  tieTolerance: number;
  /** |dz/do| below which a section sample counts as "top", not "side slope". */
  topSlopeThreshold: number;
  /** 3D Douglas-Peucker tolerance, metres. */
  simplifyTolerance: number;
  /** Runs shorter than this are discarded, metres. */
  minRunLength: number;
}

export const DEFAULT_CREST_PARAMS: CrestParams = {
  stationInterval: 2,
  corridorHalfWidth: 20,
  offsetStep: 0.25,
  flatTolerance: 0.05,
  tieTolerance: 0.005,
  topSlopeThreshold: 0.15,
  simplifyTolerance: 0.02,
  minRunLength: 20,
};

/** Guide polyline: flattened XY pairs. Its Z is never used. */
export type Guide = Float64Array;

export interface GuideStation {
  x: number;
  y: number;
  /** Unit tangent. The section normal is (-ty, tx). */
  tx: number;
  ty: number;
  /** Horizontal distance from the guide's first vertex. */
  station: number;
}

export interface CrestPoint {
  x: number;
  y: number;
  z: number;
  station: number;
  /** Signed offset from the guide; negative = left. */
  offset: number;
  kind: SectionKind;
}

export interface CrestRun {
  /** Flattened XYZ after simplification. */
  points: Float64Array;
  /** 3D length, metres. */
  lengthM: number;
  /** Station of the first point. */
  startStation: number;
}

/**
 * `GUIDE_OFF_SURFACE` and `RUNS_TOO_SHORT` are deliberately distinct even
 * though both end with `runs.length === 0`. They call for opposite actions:
 * the first means no station found any surface at all (widen the corridor or
 * move the guide); the second means surface WAS found and picked, but every
 * contiguous stretch fell below `minRunLength` (a short spoil heap, or picks
 * fragmented by gaps) — where widening the corridor does nothing and
 * lowering `minRunLength` is the fix. Telling a 15 m heap to "increase the
 * corridor half-width" is advice that makes things worse.
 *
 * `OUTSIDE_BOUNDARY` is the third way to reach `runs.length === 0`, and it
 * exists for the same reason: the surface WAS found and picked, but the
 * analysis boundary the user chose excluded (nearly) every pick. Neither
 * "widen the corridor" nor "lower the minimum length" helps — the fix is to
 * change or clear the boundary.
 */
export type CrestWarning =
  | 'GUIDE_OFF_SURFACE'
  | 'RUNS_TOO_SHORT'
  | 'MOSTLY_REJECTED'
  | 'ALL_FLAT'
  | 'OUTSIDE_BOUNDARY';

export interface CrestResult {
  runs: CrestRun[];
  stationsSampled: number;
  stationsRejected: number;
  flatSections: number;
  reliefSections: number;
  /** Vertices before simplification, summed across runs. */
  rawVertexCount: number;
  warning: CrestWarning | null;
}
